import type { SolutionPageContent } from '../../types/solution'

export const retailSolution: SolutionPageContent = {
  slug: 'retail',
  hero: {
    headline: 'Run your shop with less stress and more profit',
    subheadline: 'Sell faster, keep stock under control, and know exactly what makes money.',
    card: {
      type: 'list',
      title: 'Today · Kicukiro shop',
      badge: { label: 'Live', tone: 'positive' },
      rows: [
        { label: 'Sales', sublabel: '142 receipts', value: 'RWF 486,500' },
        { label: 'Mobile money', value: 'RWF 301,200', tone: 'brand' },
        { label: 'Low stock', sublabel: 'Sugar 1kg, Rice 5kg', value: '2 items', tone: 'warning' },
      ],
      footer: { label: 'Gross profit', value: 'RWF 97,300' },
    },
  },
  challenges: [
    {
      quote: 'I don’t know what’s out of stock until a customer asks.',
      detail: 'By then the sale is gone, and they may not come back tomorrow.',
    },
    {
      quote: 'End-of-day cash never matches my sales.',
      detail: 'Counting the drawer takes an hour, and the gap is always a mystery.',
    },
    {
      quote: 'I can’t tell which products actually make a profit.',
      detail: 'Some items sell a lot but earn almost nothing after supplier costs.',
    },
  ],
  helps: [
    {
      solves: 'End-of-day cash never matches my sales',
      title: 'Fast checkout with EBM receipts',
      copy: 'Scan or tap to add items, take any payment method, and print or send an EBM receipt in seconds. Every sale is recorded, so the drawer always adds up.',
      product: 'sales-pos',
      visual: { kind: 'screen', id: 'checkout' },
    },
    {
      solves: 'I don’t know what’s out of stock',
      title: 'Live stock with low-stock alerts',
      copy: 'Stock goes down with every sale. When a product hits its reorder level, you get an alert before the shelf is empty.',
      product: 'inventory',
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Stock levels',
          badge: { label: '3 low', tone: 'warning' },
          rows: [
            { label: 'Sugar 1kg', value: '4 left', pct: 12, tone: 'negative' },
            { label: 'Rice 5kg', value: '9 left', pct: 24, tone: 'warning' },
            { label: 'Cooking oil 1L', value: '38 left', pct: 70 },
            { label: 'Maize flour 2kg', value: '52 left', pct: 86 },
          ],
          action: 'Create purchase order',
        },
      },
    },
    {
      solves: 'I don’t know what’s out of stock',
      title: 'Restock from suppliers in a few taps',
      copy: 'Turn low-stock items into a purchase order, send it to your supplier, and receive the goods straight into stock.',
      product: 'purchases',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Purchase order #PO-0418',
          meta: 'Kivu Distributors',
          badge: { label: 'Sent', tone: 'brand' },
          rows: [
            { label: 'Sugar 1kg', sublabel: '48 × RWF 1,350', value: 'RWF 64,800' },
            { label: 'Rice 5kg', sublabel: '20 × RWF 7,200', value: 'RWF 144,000' },
          ],
          footer: { label: 'Total', value: 'RWF 208,800' },
        },
      },
    },
    {
      solves: 'I can’t tell which products make a profit',
      title: 'Profit by product, every day',
      copy: 'See revenue, cost, and margin for each product. Push what earns and rethink what doesn’t.',
      product: 'reports',
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Top products by profit · September',
          rows: [
            { label: 'Cooking oil 1L', value: 'RWF 184,000', pct: 100, tone: 'positive' },
            { label: 'Rice 5kg', value: 'RWF 142,500', pct: 77, tone: 'positive' },
            { label: 'Soap bar', value: 'RWF 61,200', pct: 33 },
            { label: 'Bottled water', value: 'RWF 12,400', pct: 7, tone: 'warning' },
          ],
        },
      },
    },
  ],
  journey: {
    heading: 'A day with Kayko',
    subheading: 'How a typical shop day runs when everything is in one place.',
    items: [
      { time: '7:00', title: 'Open the shop', copy: 'Open the till on your phone or tablet with yesterday’s float ready.' },
      { time: '8:00–19:00', title: 'Sell and accept payments', copy: 'Ring up sales and take mobile money, card, or cash.' },
      { time: '14:00', title: 'Get low-stock alerts', copy: 'Kayko flags the products that are running out.' },
      { time: '15:00', title: 'Reorder from suppliers', copy: 'Send a purchase order in a few taps.' },
      { time: '20:00', title: 'Close the day', copy: 'Check the sales report and cash-up before you lock up.' },
    ],
  },
  results: [
    { value: '9 sec', label: 'average checkout time per customer' },
    { value: '−60%', label: 'fewer stock-outs on best-selling products' },
    { value: '1 hr', label: 'saved every evening on cash-up' },
  ],
  testimonial: {
    quote: 'Now I see what’s running low before my customers do, and my evening cash-up takes ten minutes.',
    name: 'Jemima Nyirasafari',
    role: 'Owner',
    business: 'All-in-one supermarket',
    location: 'Kicukiro, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/26e15333-45ec-4421-ac04-24cf98518008.jpg',
    metric: { value: '−60%', label: 'fewer stock-outs in her first three months' },
  },
  products: ['sales-pos', 'inventory', 'purchases', 'reports'],
  plan: { name: 'Starter', audience: 'retail businesses' },
  faq: [
    {
      question: 'Can I use a barcode scanner?',
      answer: 'Yes. Connect a USB or Bluetooth scanner, or use your phone’s camera. Add barcodes to products once and scan them at checkout.',
    },
    {
      question: 'Can I sell on credit to regular customers?',
      answer: 'Yes. Record a sale on credit against a customer, and Kayko tracks their balance and payments. You can send them a statement by SMS.',
    },
    {
      question: 'How do I handle returns?',
      answer: 'Find the original receipt, choose the items being returned, and refund by the original payment method. Stock is added back automatically.',
    },
    {
      question: 'Are my receipts EBM compliant?',
      answer: 'Yes. Kayko issues RRA EBM-compliant receipts on every plan, printed or sent digitally.',
    },
    {
      question: 'Can I control what staff can do at the till?',
      answer: 'Give each cashier their own login. Limit discounts, refunds, and price changes, and see sales by staff member.',
    },
  ],
  related: ['food-beverage', 'manage-inventory', 'sell-and-get-paid'],
  cta: {
    headline: 'Give your shop a clearer view of every sale',
    subheadline: 'Set up in minutes. Local support in Kigali when you need it.',
  },
}
